"use client"

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  useReactTable,
  ColumnSort,
  getSortedRowModel,
} from "@tanstack/react-table"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Input } from "@/components/form/input"
import { Button } from "../ui/button"
import classNames from "classnames"
import { useCallback, useState } from "react"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  SortAsc,
  SortDesc,
} from "lucide-react"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  onRowClick?: (row: TData) => void
  searchFields?: string[]
  withSorting?: boolean
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  searchFields,
  withSorting = false,
}: DataTableProps<TData, TValue>) {
  const [globalFilter, setGlobalFilter] = useState("")
  const [sorting, setSorting] = useState<ColumnSort[]>([])

  // Custom filter function that searches across specified fields
  const globalFilterFn = useCallback(
    (row: any, columnId: string, value: string) => {
      if (!searchFields || searchFields.length === 0 || !value) {
        return true
      }

      const searchValue = value.toLowerCase()
      return searchFields.some((field) => {
        const fieldValue = row.original[field]
        return (
          fieldValue && String(fieldValue).toLowerCase().includes(searchValue)
        )
      })
    },
    [searchFields],
  )
  const table = useReactTable({
    enableHiding: true,
    enableSorting: withSorting,
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn,
    state: {
      globalFilter,
      sorting,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },
  })

  return (
    <>
      {searchFields && searchFields.length > 0 && (
        <div className="mt-6 mb-6">
          <Input
            placeholder="Meklēt"
            value={globalFilter ?? ""}
            onChange={(event) => setGlobalFilter(event.target.value)}
          />
        </div>
      )}
      <div className="rounded-md border mt-5">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        cursor: header.column.getCanSort()
                          ? "pointer"
                          : "default",
                        color: header.column.getIsSorted() ? "var(--iris)" : "",
                      }}
                    >
                      <span className="flex row gap-1 items-center">
                        {header.column.getCanSort()
                          ? {
                              desc: <ArrowUp size={12} />,
                              asc: <ArrowDown size={12} />,
                              none: <ArrowUpDown size={12} />,
                              // @ts-ignore
                            }[header.column.getIsSorted() || "none"]
                          : null}
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </span>
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="text-base">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() => onRowClick?.(row.original)}
                  className={classNames(
                    onRowClick !== undefined && "cursor-pointer",
                    // @ts-ignore
                    row.original.deletedAt && "opacity-50",
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="[&_a]:text-[var(--iris)]"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center "
                >
                  Nav rezultātu
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {data.length && data.length > 25 ? (
        <div className="flex items-center justify-center mt-5 gap-2">
          <Button
            variant="outline"
            onClick={() => table.firstPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {"<<"}
          </Button>
          <Button
            variant="outline"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {"<"}
          </Button>
          <Button
            variant="outline"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {">"}
          </Button>
          <Button
            variant="outline"
            onClick={() => table.lastPage()}
            disabled={!table.getCanNextPage()}
          >
            {">>"}
          </Button>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value))
            }}
          >
            {[25, 50, 150].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
      ) : null}
    </>
  )
}
